import React, { useEffect, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, InfoIcon, ChevronRightIcon, HomeIcon, AlertCircleIcon, ShieldCheckIcon } from 'lucide-react';
import { getArticleBySlug } from '../data/articles';
import { RelatedArticles } from '../components/RelatedArticles';
import { Newsletter } from '../components/Newsletter';
import { CommentsSection } from '../components/CommentsSection';
export function ArticleDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center" data-id="element-227">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-228">
          <h1 className="font-heading text-4xl font-bold text-vita-green-deep mb-4" data-id="element-229">
            Artikel nicht gefunden
          </h1>
          <p className="text-vita-text-muted mb-8 text-lg" data-id="element-230">
            Der gesuchte Artikel existiert leider nicht.
          </p>
          <Link to="/artikel" className="inline-flex items-center px-6 py-3 bg-vita-green-deep text-white rounded-full hover:bg-vita-green-medium transition-colors" data-id="element-231">
            Alle Artikel ansehen
          </Link>
        </motion.div>
      </div>;
  }
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  // Build a map of images keyed by afterSection index
  const imageMap = new Map<number, {
    src: string;
    alt: string;
  }>();
  if (article.images) {
    for (const img of article.images) {
      imageMap.set(img.afterSection, {
        src: img.src,
        alt: img.alt
      });
    }
  }
  return <>
      {/* Hero Area */}
      <section className={`relative bg-gradient-to-br ${article.bgGradient} pt-8 pb-16 md:pt-12 md:pb-24`} data-id="element-232">
        <div className="absolute inset-0 opacity-10 mix-blend-multiply pointer-events-none" data-id="element-233">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-234">
            <defs data-id="element-235">
              <pattern id="article-hero-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse" data-id="element-236">
                <circle cx="30" cy="30" r="2" fill="currentColor" className="text-vita-green-sage" data-id="element-237" />
              </pattern>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="url(#article-hero-pattern)" data-id="element-238" />
          </svg>
        </div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-239">
          {/* Breadcrumb */}
          <motion.nav initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.4
        }} aria-label="Breadcrumb" className="mb-8" data-id="element-240">
            <ol className="flex items-center flex-wrap gap-1 text-sm text-vita-text-muted" data-id="element-241">
              <li className="flex items-center" data-id="element-242">
                <Link to="/" className="hover:text-vita-green-deep transition-colors flex items-center" data-id="element-243">
                  <HomeIcon className="w-4 h-4 mr-1" data-id="element-244" />
                  Home
                </Link>
              </li>
              <li className="flex items-center" data-id="element-245">
                <ChevronRightIcon className="w-4 h-4 mx-1 text-vita-text-light" data-id="element-246" />
                <Link to="/artikel" className="hover:text-vita-green-deep transition-colors" data-id="element-247">
                  Artikel
                </Link>
              </li>
              <li className="flex items-center" data-id="element-248">
                <ChevronRightIcon className="w-4 h-4 mx-1 text-vita-text-light" data-id="element-249" />
                <span className="text-vita-text font-medium truncate max-w-[200px]" data-id="element-250">
                  {article.category}
                </span>
              </li>
            </ol>
          </motion.nav>

          {/* Title Area */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-251">
            <span className={`inline-block px-4 py-1.5 text-sm font-medium ${article.categoryText} ${article.categoryBg} rounded-full mb-6`} data-id="element-252">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-vita-text leading-tight mb-4" data-id="element-253">
              {article.title}
            </h1>

            {/* Subtitle — data-driven */}
            {article.subtitle && <p className="text-vita-text-muted text-base md:text-lg leading-relaxed mb-6" data-id="element-254">
                {article.subtitle}
              </p>}

            <p className="text-vita-text-muted text-sm" data-id="element-255">{article.readTime}</p>

            {/* Hero Image — data-driven */}
            {article.heroImage && <div className="mt-8 rounded-softer overflow-hidden shadow-vita" data-id="element-256">
                <img src={article.heroImage} alt={article.title} className="w-full h-auto object-cover" data-id="element-257" />
              </div>}
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-16 bg-white" data-id="element-258">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-259">
          {/* Intro */}
          <motion.p variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-lg md:text-xl text-vita-text leading-relaxed mb-12 whitespace-pre-line" data-id="element-260">
            {'In den morgendlichen und abendlichen Sto\xDFzeiten in U-Bahn und Stadtbussen sieht man immer h\xE4ufiger dasselbe Bild: ersch\xF6pfte Frauen, die f\xFCr einen Moment die Augen schlie\xDFen. Auf ihren Gesichtern liegt gleichzeitig Anspannung und Kontrolle \u2013 als w\xFCrden sie versuchen, kurz abzuschalten, ohne ihre Haltestelle zu verpassen. Was fr\xFCher auffiel, ist heute kaum noch bemerkenswert. F\xFCr viele Frauen ist Schlafmangel l\xE4ngst kein Ausnahmezustand mehr, sondern ein stiller, t\xE4glicher Begleiter geworden.'}
          </motion.p>

          <motion.p variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-lg md:text-xl text-vita-text leading-relaxed mb-12 whitespace-pre-line" data-id="element-261">
            {'Im Februar 2025 ver\xF6ffentlichte der Verkehrsverbund Berlin-Brandenburg gemeinsam mit der Plattform \u201EFocus Online\u201C Ergebnisse einer Umfrage unter Fahrg\xE4sten. Dabei gaben rund 42% der Befragten an, dass sie im \xF6ffentlichen Nahverkehr nicht aus Langeweile d\xF6sen, sondern aus chronischem Schlafmangel \u2013 und besonders h\xE4ufig betrifft das Frauen. Was wie ein individuelles Problem wirkt, zeigt sich hier als weit verbreitete Realit\xE4t.'}
          </motion.p>

          <motion.p variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-lg md:text-xl text-vita-text leading-relaxed mb-12 whitespace-pre-line" data-id="element-262">
            {'Mitte M\xE4rz 2026 gingen in sozialen Netzwerken Videos aus deutschen Gro\xDFst\xE4dten viral, auf denen Frauen in der U-Bahn und S-Bahn pl\xF6tzlich einschlafen. Sie steigen ein, setzen sich auf den n\xE4chsten freien Platz, ziehen eine schlichte Schlafmaske \xFCber die Augen \u2013 und wirken, als w\xFCrden sie f\xFCr einen Moment komplett abschalten. F\xFCr ein paar Minuten entziehen sie sich dem L\xE4rm der Bahn, dem Druck des Arbeitstags, den unerledigten Aufgaben und der st\xE4ndigen Verantwortung. Es ist kein Zeichen von Bequemlichkeit, sondern von Ersch\xF6pfung \u2013 und davon, wie sehr viele Frauen im Alltag an ihre Grenzen gehen.'}
          </motion.p>

          <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="mb-12 rounded-softer overflow-hidden shadow-vita" data-id="element-263">
            <img src="https://cdn.magicpatterns.com/uploads/9ewdDqgVaZEg3fqTgpTBFH/3.png" alt="Frau mit Schlafmaske in der U-Bahn – Aufschrift: Nur noch 5 Minuten" className="w-full h-auto object-cover" data-id="element-264" />
          </motion.div>

          {/* Highlighted Statement */}

          {/* Key Takeaways */}

          {/* Content Sections */}
          {article.sections.map((section, idx) => <Fragment key={idx} data-id="element-265">
              <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="mb-10" data-id="element-266">
                <div className="space-y-4" data-id="element-267">
                  {section.content.split('\n\n').map((paragraph, pIdx) => paragraph.startsWith('Und genau hier liegt das Problem:') ? <div key={pIdx} className="bg-[#F0EBF5]/60 border-l-4 border-[#8B7BAF] rounded-r-softer p-5 md:p-6 my-2" data-id="element-268">
                        <p className="text-vita-text font-semibold leading-relaxed text-base md:text-lg" data-id="element-269">
                          {paragraph}
                        </p>
                      </div> : paragraph.includes('dass schon kleine Fehlstellungen die Entspannung verhindern') ? <Fragment key={pIdx} data-id="element-270">
                        <p className="text-vita-text-muted leading-relaxed text-base md:text-lg" data-id="element-271">
                          {paragraph}
                        </p>
                        <motion.div initial={{
                  opacity: 0,
                  y: 12
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.1
                }} className="relative bg-gradient-to-r from-[#FDF6EC] to-[#FFF9F0] border-l-4 border-[#D4A853] rounded-r-softer p-5 md:p-7 my-4 shadow-sm" data-id="element-272">
                          <span className="absolute -top-3 left-4 bg-[#D4A853] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm" data-id="element-273">
                            Wichtig
                          </span>
                          <p className="text-vita-text font-semibold leading-relaxed text-base md:text-lg mt-2" data-id="element-274">
                            Das eigentliche Problem: Die meisten Kissen – selbst
                            sogenannte orthopädische Modelle – wurden lange Zeit
                            nicht speziell für die weibliche Anatomie entwickelt
                            oder getestet. Sie orientieren sich eher an einer
                            stabileren, breiteren Körperstruktur. In der Praxis
                            bedeutet das, dass viele Frauen auf Kissen schlafen,
                            die nicht zu ihrem Körper passen.
                          </p>
                        </motion.div>
                        <p className="text-vita-text-muted leading-relaxed text-base md:text-lg" data-id="element-275">
                          Die Folge ist, dass der Kopf im falschen Winkel liegt,
                          der Nacken nicht richtig gestützt wird und die
                          Muskulatur die ganze Nacht unter Spannung bleibt. Der
                          Schlaf wird oberflächlich, man wacht häufiger auf und
                          fühlt sich morgens trotz ausreichender Schlafdauer
                          erschöpft.
                        </p>
                        <p className="text-vita-text-muted leading-relaxed text-base md:text-lg" data-id="element-276">
                          Über Wochen und Monate verstärkt sich dieser Effekt.
                          Es entsteht das Gefühl von dauerhaftem Schlafmangel,
                          obwohl man eigentlich genug schläft.
                        </p>
                        <p className="text-vita-text-muted leading-relaxed text-base md:text-lg" data-id="element-277">
                          Deshalb setzen immer mehr Frauen gezielt auf
                          ergonomische Kissen, die an die natürliche Form von
                          Nacken und Schultern angepasst sind. Sie sorgen dafür,
                          dass der Kopf stabil liegt, der Nacken entlastet wird
                          und der Körper endlich in eine tiefere Entspannung
                          kommt. Genau hier liegt für viele der entscheidende
                          Unterschied zwischen „schlafen" und „wirklich erholt
                          aufwachen".
                        </p>
                        {article.ctaButton && <div className="mt-8 mb-3" data-id="element-278">
                            <a href={article.ctaButton.link} className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-vita-green-deep to-vita-green-medium text-white font-bold rounded-full hover:from-vita-green-medium hover:to-vita-green-deep transition-all duration-300 text-base md:text-lg shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-[0.98]" data-id="element-279">
                              👉 Beispiel für ein passendes Kissen ansehen
                              <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" data-id="element-280" />
                            </a>
                          </div>}
                      </Fragment> : paragraph.startsWith('Das eigentliche Problem:') ? <div key={pIdx} className="relative bg-gradient-to-r from-[#FDF6EC] to-[#FFF9F0] border-l-4 border-[#D4A853] rounded-r-softer p-5 md:p-7 my-4 shadow-sm" data-id="element-281">
                        <span className="absolute -top-3 left-4 bg-[#D4A853] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm" data-id="element-282">
                          Wichtig
                        </span>
                        <p className="text-vita-text font-semibold leading-relaxed text-base md:text-lg mt-2" data-id="element-283">
                          {paragraph}
                        </p>
                      </div> : <p key={pIdx} className="text-vita-text-muted leading-relaxed text-base md:text-lg" data-id="element-284">
                        {paragraph}
                      </p>)}
                </div>

                {/* Quote from affected women — after third section (idx 2) */}
                {idx === 2 && <blockquote className="mt-8 bg-gray-50 rounded-softer p-6 md:p-8 border-l-4 border-gray-300" data-id="element-285">
                    <p className="text-vita-text leading-relaxed text-base md:text-lg italic" data-id="element-286">
                      „Nach der Arbeit beginnt für viele Frauen die zweite
                      Schicht: kochen, aufräumen, organisieren, funktionieren.
                      Kinder ins Bett bringen, an den nächsten Tag denken, alles
                      im Kopf behalten. Es hört einfach nicht auf. Selbst abends
                      gibt es keine echte Pause – nur das Gefühl, noch mehr
                      erledigen zu müssen. Und irgendwo dazwischen verschwindet
                      der Schlaf"
                    </p>
                    <footer className="mt-3 text-sm text-vita-text-light" data-id="element-287">
                      – berichten betroffene Frauen
                    </footer>
                  </blockquote>}

                {/* Sleep tips — after fifth section (idx 4) */}
                {idx === 4 && <div className="mt-6" data-id="element-288">
                    <h3 className="font-heading text-lg md:text-xl font-bold text-vita-text mb-5" data-id="element-289">
                      Was wirklich gegen schlechten Schlaf hilft:
                    </h3>
                    <div className="space-y-3" data-id="element-290">
                      {[{
                  title: 'Fester Schlafrhythmus',
                  desc: 'Jeden Tag zur gleichen Zeit schlafen und aufstehen.'
                }, {
                  title: 'Abends weniger Bildschirmzeit',
                  desc: 'Licht von Displays stört die Melatoninproduktion.'
                }, {
                  title: 'Gedanken vor dem Schlafen beruhigen',
                  desc: 'To-Do-Listen aufschreiben, Kopf entlasten.'
                }, {
                  title: 'Kühle, ruhige Umgebung',
                  desc: '17–19°C und möglichst dunkel schlafen.'
                }, {
                  title: 'Koffein reduzieren',
                  desc: 'Mindestens 6 Stunden vor dem Schlaf vermeiden.'
                }, {
                  title: 'Körperhaltung im Schlaf wird oft unterschätzt',
                  desc: 'Viele konzentrieren sich darauf, wie lange sie schlafen – aber kaum jemand darauf, was im Körper währenddessen passiert.'
                }].map((tip, tIdx) => <div key={tIdx} className="flex items-start" data-id="element-291">
                          <span className="w-1.5 h-1.5 rounded-full bg-vita-green-sage mt-2.5 mr-3 flex-shrink-0" data-id="element-292"></span>
                          <p className="text-vita-text-muted leading-relaxed text-base md:text-lg" data-id="element-293">
                            <span className="font-medium text-vita-text" data-id="element-294">
                              {tip.title}
                            </span>
                            {' – '}
                            {tip.desc}
                          </p>
                        </div>)}
                    </div>
                  </div>}

                {/* Photo after section content — shown after second section (idx 1) */}
                {idx === 1 && <div className="mt-6 rounded-softer overflow-hidden shadow-vita" data-id="element-295">
                    <img src="https://cdn.magicpatterns.com/uploads/mchDuTa5LnaV3PjWSGCDY8/2.png" alt="Frau mit Schlafmaske in der S-Bahn – Aufschrift: Ich schlafe noch 20 Minuten" className="w-full h-auto object-cover" data-id="element-296" />
                  </div>}

                {/* Highlighted mask quote — after first section */}
                {idx === 0 && <>
                    <motion.p variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} className="mt-6 text-lg md:text-xl font-semibold text-vita-green-deep leading-snug pl-5 border-l-4 border-vita-green-sage italic" data-id="element-297">
                      Auf manchen Masken stehen Sätze wie „Nur noch 5 Minuten…"
                      oder „Ich schlafe noch 20 Minuten"
                    </motion.p>
                    <p className="mt-4 text-vita-text-muted leading-relaxed text-base md:text-lg" data-id="element-298">
                      Die Auflösung liegt im Datum: Die Szenen tauchten rund um
                      den 13. März auf – den Weltschlaftag. Ziel der Aktion war
                      es, auf ein Problem aufmerksam zu machen, das besonders
                      viele Frauen betrifft und im Alltag oft unterschätzt wird:
                      fehlender, nicht erholsamer Schlaf.
                    </p>
                  </>}
              </motion.div>

              {/* Inline Image — after specified section index */}
              {imageMap.has(idx) && <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="mb-10 rounded-softer overflow-hidden shadow-vita" data-id="element-299">
                  <img src={imageMap.get(idx)!.src} alt={imageMap.get(idx)!.alt} className="w-full h-auto object-cover" data-id="element-300" />
                </motion.div>}

              {/* Mid-Article CTA — after specified section index */}
              {article.midArticleCtaAfterSection !== undefined && article.ctaButton && idx === article.midArticleCtaAfterSection}
            </Fragment>)}

          {/* Was bedeutet das für dich? */}

          {/* Product Integration Section */}
          {article.productIntegration && <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="mb-10" data-id="element-301"></motion.div>}

          {/* Suggestion Box */}
          <motion.div variants={sectionVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="bg-vita-peach/50 rounded-softer p-6 md:p-8 border border-vita-orange-pale mb-8" data-id="element-302">
            <h3 className="font-heading text-lg font-bold text-vita-orange mb-3 flex items-center" data-id="element-303">
              <InfoIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-304" />
              {article.suggestionTitle}
            </h3>
            <p className="text-vita-text-muted leading-relaxed" data-id="element-305">
              {'Immer mehr Frauen berichten, dass sie sich endlich wieder erholt f\xFChlen \u2013 vor allem dann, wenn sie auf ein ergonomisches Kissen umsteigen und in einer k\xFChleren Umgebung (unter 20 \xB0C) schlafen. \n\nWeniger Verspannungen, ruhigerer Schlaf und das Gef\xFChl, morgens wirklich ausgeruht und mit mehr Energie in den Tag zu starten.'}
            </p>
          </motion.div>

          {/* Interactive Comments Section */}
          <CommentsSection articleSlug={article.slug} data-id="element-306" />

          {/* Trust Frame */}
          {article.trustPoints && article.trustPoints.length > 0}

          {/* Medical Disclaimer */}
        </div>
      </article>

      {/* Related Articles */}
      <div className="bg-vita-cream" data-id="element-307">
        <RelatedArticles currentSlug={article.slug} relatedSlugs={article.relatedSlugs} data-id="element-308" />
      </div>

      {/* Newsletter */}
      <Newsletter data-id="element-309" />
    </>;
}